import { isNativeApp } from '@/utils/platform';

/**
 * Consent gate for non-essential tracking (GDPR / PECR).
 *
 * Everything the app needs to function — the auth token, the theme, listening
 * progress (see cookieUtils) — is strictly necessary and stays outside this
 * gate: it is storage the user asked for by using the product, and no consent
 * is required for it. What *is* gated is product analytics, which today means
 * Mixpanel and the identifiers it persists in localStorage. Nothing reaches
 * Mixpanel until the visitor accepts; see services/analytics.ts, the single
 * place that reads this.
 *
 * The default is `unset`, which behaves exactly like `denied` for tracking
 * purposes — consent has to be given, never assumed, so a visitor who ignores
 * the banner is never tracked. The one exception is the grandfathering rule
 * below, which is a deliberate product decision rather than a legal position.
 */

export type ConsentStatus = 'granted' | 'denied' | 'unset';

export interface ConsentSnapshot {
  /** What the visitor has decided about analytics. */
  status: ConsentStatus;
  /** Whether the consent banner should currently be on screen. */
  promptOpen: boolean;
  /**
   * What produced the current status: a choice the visitor just made here, the
   * server's stored record, or nothing yet. The app pushes a decision to the
   * API only when it came from the visitor — reconciling a value the server
   * just gave us and sending it straight back would be a pointless round trip.
   */
  source: 'user' | 'server' | null;
}

const STORAGE_KEY = 'aleph:analytics-consent';

/**
 * Bump when what we ask consent for changes — a new analytics provider, a new
 * purpose. Decisions recorded under an older version read back as `unset`, so
 * the banner asks again instead of silently reusing a consent the visitor never
 * gave for the new processing.
 */
const CONSENT_VERSION = 1;

interface StoredConsent {
  version: number;
  analytics: boolean;
  decidedAt: string;
}

/**
 * The native shell never renders the marketing home page, so it has no surface
 * to show the banner on and no way to collect a decision. Gating analytics
 * there would silently switch iOS telemetry off with nothing to switch it back
 * on, so native reports as granted and keeps its current behaviour. Giving iOS
 * its own consent surface is a separate piece of work.
 */
const NATIVE_SNAPSHOT: ConsentSnapshot = { status: 'granted', promptOpen: false, source: null };

/**
 * Whether the visitor arrived already signed in.
 *
 * Shipping the gate without this would switch analytics off for the entire
 * existing user base, none of whom were ever asked. An account that predates
 * the banner is therefore treated as consenting until it says otherwise.
 *
 * Note what this is: consent assumed, not given. An explicit decision always
 * wins over it, and it can be withdrawn at any time through the Cookies link
 * in the footer, which reopens the banner (see openConsentPreferences).
 *
 * Read once at module load, which is before anything in this page load can log
 * in. That is what keeps it to *pre-existing* accounts: someone who ignores the
 * banner and then signs up is not grandfathered, because at load they had no
 * session.
 *
 * The keys are authService's, read directly rather than through it: authService
 * imports analytics, which imports this module, and going through it would
 * close that cycle. Mirrors hasStoredCredentials() — an expired token still
 * means a returning user.
 */
const detectExistingSession = (): boolean => {
  try {
    return Boolean(
      localStorage.getItem('aleph_access_token') && localStorage.getItem('aleph_user')
    );
  } catch {
    return false;
  }
};

const grandfathered = detectExistingSession();

const listeners = new Set<() => void>();

// useSyncExternalStore compares snapshots by identity, so the object is built
// once per change and handed out until something invalidates it.
let snapshot: ConsentSnapshot | null = null;

// The decision as taken this page load. Survives a localStorage write that
// failed (private mode, storage full): the choice still holds until reload,
// rather than the banner reappearing on the next render.
let sessionStatus: ConsentStatus | null = null;

// Set when the visitor reopens their choice from the footer, so the banner
// comes back even though a decision is already on record.
let reopened = false;

let lastSource: ConsentSnapshot['source'] = null;

const readStoredStatus = (): ConsentStatus => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'unset';

    const parsed = JSON.parse(raw) as Partial<StoredConsent> | null;
    if (parsed?.version !== CONSENT_VERSION || typeof parsed.analytics !== 'boolean') {
      return 'unset';
    }
    return parsed.analytics ? 'granted' : 'denied';
  } catch {
    // Storage unavailable or the entry is corrupt. Treat it as undecided: with
    // no readable record we cannot claim a decision was made.
    return 'unset';
  }
};

const notify = (): void => {
  snapshot = null;
  listeners.forEach((listener) => listener());
};

const resolveStatus = (): ConsentStatus => {
  if (sessionStatus) return sessionStatus;

  const stored = readStoredStatus();
  if (stored !== 'unset') return stored;

  // Only ever upgrades the absence of a decision. A visitor who rejected has a
  // stored 'denied' and never reaches this line.
  return grandfathered ? 'granted' : 'unset';
};

export const getConsentSnapshot = (): ConsentSnapshot => {
  if (isNativeApp()) return NATIVE_SNAPSHOT;

  if (!snapshot) {
    const status = resolveStatus();
    snapshot = { status, promptOpen: reopened || status === 'unset', source: lastSource };
  }
  return snapshot;
};

export const getConsentStatus = (): ConsentStatus => getConsentSnapshot().status;

/** The one question the analytics layer asks. Undecided means no. */
export const hasAnalyticsConsent = (): boolean => getConsentStatus() === 'granted';

/**
 * Record the visitor's decision. Accepting and rejecting are the same single
 * click; rejecting is not made harder than accepting, and a rejection is
 * recorded (rather than left unset) so we stop asking.
 */
export const setAnalyticsConsent = (granted: boolean): void => {
  if (isNativeApp()) return;

  sessionStatus = granted ? 'granted' : 'denied';
  lastSource = 'user';
  reopened = false;

  try {
    const record: StoredConsent = {
      version: CONSENT_VERSION,
      analytics: granted,
      decidedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Best effort — the decision holds for this page load either way.
  }

  notify();
};

/**
 * Adopt the decision stored on the user's account.
 *
 * The account is authoritative: it is what the API gates on, and it is the only
 * copy that follows the user between devices. A browser that has never been
 * answered in — or one grandfathered into consent it never actually gave — must
 * not keep tracking locally after the account says no.
 *
 * Marked as server-sourced so the app does not turn around and POST it back.
 */
export const adoptServerDecision = (granted: boolean): void => {
  if (isNativeApp()) return;

  const status: ConsentStatus = granted ? 'granted' : 'denied';
  if (getConsentStatus() === status) return;

  sessionStatus = status;
  lastSource = 'server';
  reopened = false;

  try {
    const record: StoredConsent = {
      version: CONSENT_VERSION,
      analytics: granted,
      decidedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Best effort — the decision holds for this page load either way.
  }

  notify();
};

/** Reopen the banner so a decision can be changed (withdrawing consent). */
export const openConsentPreferences = (): void => {
  if (isNativeApp()) return;
  reopened = true;
  notify();
};

/**
 * The decision in the shape the API expects: `null` when the visitor has not
 * answered yet.
 *
 * Null matters — it means "no answer to give", not "no". The server leaves a
 * stored decision untouched when it receives null, so signing in from a fresh
 * browser cannot silently revoke a consent given on another device.
 */
export const getAnalyticsConsentForServer = (): boolean | null => {
  const status = getConsentStatus();
  return status === 'unset' ? null : status === 'granted';
};

export const subscribeToConsent = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// A decision taken in another tab applies here too — withdrawing consent should
// not need a reload of every open tab to take effect.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    sessionStatus = null;
    notify();
  });
}
